import { useState, useCallback } from 'react';
import { useAuth, type User } from './AuthContext';

interface GoogleLoginResponse {
  success: boolean;
  message: string;
  data: {
    refresh: string;
    access: string;
    user: User;
  };
}

export function useGoogleLogin() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();

  const handleGoogleLogin = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    if (!GOOGLE_CLIENT_ID) {
      setError('Google Client ID not configured');
      setIsLoading(false);
      return null;
    }

    try {
      return new Promise<GoogleLoginResponse['data']>((resolve, reject) => {
        const client = google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: async (tokenResponse: any) => {
            if (tokenResponse.error) {
              setError(tokenResponse.error);
              setIsLoading(false);
              reject(tokenResponse.error);
              return;
            }

            const accessToken = tokenResponse.access_token;
            
            if (!accessToken) {
              const err = 'No access token received from Google';
              setError(err);
              setIsLoading(false);
              reject(err);
              return;
            }

            try {
              const API_URL = import.meta.env.VITE_API_URL;
              
              const response = await fetch(`${API_URL}/api/auth/google-login/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ access_token: accessToken }),
              });

              const result: GoogleLoginResponse = await response.json();

              if (!response.ok || !result.success) {
                throw new Error(result.message || 'Google login failed');
              }

              login(result.data.user, result.data.access, result.data.refresh);
              
              setIsLoading(false);
              resolve(result.data);
            } catch (err) {
              const message = (err as Error).message;
              setError(message);
              setIsLoading(false);
              reject(err);
            }
          },
        });

        client.requestAccessToken({ prompt: 'consent' });
      });
    } catch (err) {
      const message = (err as Error).message;
      setError(message);
      setIsLoading(false);
      throw err;
    }
  }, [login]);

  return {
    handleGoogleLogin,
    isLoading,
    error,
  };
}

declare const google: any;
